import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  ListItemAvatar,
  ListItemText,
  Divider,
  alpha,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  SupportAgent as SupportAgentIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Router as RouterIcon,
} from '@mui/icons-material';

import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { ThemeMenuButton } from './ThemeMenuButton';
import { getHeaderAppBarBackground } from '../styles/theme';
import { isCopaTheme } from '../themes/themeRegistry';
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED, MOBILE_APP_BAR_HEIGHT, DESKTOP_APP_BAR_HEIGHT } from './sidebarStyles';
import { UserAvatar } from '../components/common';
import { clientService } from '../services/clientService';
import equipmentService from '../services/equipmentService';
import invoiceService from '../services/invoiceService';
import paymentService from '../services/paymentService';
import InvoiceViewModal from '../components/modals/InvoiceViewModal';
import logo from '../assets/logo.png';
import logoWhite from '../assets/logo-white.png';
import { formatarData, formatarValor } from '../utils/formatToView';

// Estilos do Toolbar
const toolbarStyles = {
  main: {
    minHeight: {
      xs: MOBILE_APP_BAR_HEIGHT,
      sm: DESKTOP_APP_BAR_HEIGHT,
    },
    px: {
      xs: 2, // Mobile: padding menor
      sm: 3, // Desktop: padding padrão
    },
  },
};

// AppBar alinhado à direita do sidebar (não sobrepõe)
const getAppBarLayoutSx = (theme: Theme, isMobile: boolean, sidebarExpanded: boolean) => {
  const sidebarW = sidebarExpanded ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_COLLAPSED;
  return {
    position: 'fixed' as const,
    width: isMobile ? '100%' : `calc(100% - ${sidebarW}px)`,
    ml: isMobile ? 0 : `${sidebarW}px`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    zIndex: theme.zIndex.drawer + 1,
  };
};

// Estilos do Campo de Pesquisa
const searchFieldStyles = {
  container: {
    maxWidth: 600,
    width: '100%',
    position: 'relative' as const,
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      borderRadius: 25,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.25)',
      },
      '&.Mui-focused': {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
        '& input': {
          color: (theme: any) => theme.palette.text.primary,
        },
        '& input::placeholder': {
          color: (theme: any) => theme.palette.text.secondary,
          opacity: 0.7,
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'transparent',
      },
    },
    '& input': {
      color: 'white',
      fontSize: '0.9rem',
    },
    '& input::placeholder': {
      color: 'rgba(255, 255, 255, 0.7)',
      opacity: 1,
    },
  },
  searchIcon: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  clearButton: {
    color: 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
      color: 'white',
    },
    mr: 0.5,
  },
  searchButton: (hasValue: boolean) => ({
    color: hasValue ? 'white' : 'rgba(255, 255, 255, 0.3)',
    '&:hover': {
      color: 'white',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&:disabled': {
      color: 'rgba(255, 255, 255, 0.3)',
    },
  }),
};

// Estilos do Menu de Resultados
const searchMenuStyles = {
  paper: (theme: any) => ({
    elevation: 8,
    overflow: 'visible',
    filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.15))',
    mt: 0.5,
    minWidth: 350,
    maxWidth: 450,
    maxHeight: 400,
    zIndex: theme.zIndex.modal - 100,
    '& .MuiMenuItem-root': {
      minHeight: 60,
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-child': {
        borderBottom: 'none',
      },
      '&:hover': {
        backgroundColor: 'action.hover',
      },
    },
  }),
  menuList: {
    py: 0,
  },
  resultAvatar: {
    bgcolor: 'transparent',
    width: 40,
    height: 40,
  },
  resultType: {
    textTransform: 'capitalize' as const,
    fontWeight: 'medium',
    alignSelf: 'center',
  },
};

// Estilos do Logo
const logoStyles = {
  height: {
    xs: 42, // Mobile: altura menor
    sm: 45, // Desktop: altura padrão
  },
  cursor: 'pointer',
};

// Estilos do Botão de Menu
const menuButtonStyles = (theme: any, isDarkMode: boolean, isCopa = false) => ({
  ml: 0,
  display: 'flex',
  backgroundColor: isDarkMode ? alpha('#fff', 0.1) : isCopa ? alpha('#013f1e', 0.08) : 'white',
  color: isDarkMode ? '#e2e8f0' : theme.palette.primary.main,
  borderRadius: 2,
  width: 30,
  height: 30,
  border: isDarkMode
    ? `1px solid ${alpha('#3b82f6', 0.3)}`
    : isCopa
      ? `1px solid ${alpha('#013f1e', 0.15)}`
      : 'none',
  '&:hover': {
    backgroundColor: isDarkMode
      ? alpha('#3b82f6', 0.22)
      : isCopa
        ? alpha('#013f1e', 0.14)
        : 'rgba(255, 255, 255, 0.9)',
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  transition: 'all 0.2s ease-in-out',
  boxShadow: isDarkMode ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
});

const headerActionsGroupSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.25,
  ml: 1,
};

const headerActionButtonSx = (isDarkMode: boolean, isCopa = false) => ({
  color: 'inherit',
  borderRadius: 2,
  ...(isDarkMode
    ? {
        '&:hover': {
          bgcolor: alpha('#3b82f6', 0.2),
        },
      }
    : isCopa
      ? {
          '&:hover': {
            bgcolor: alpha('#013f1e', 0.08),
          },
        }
      : {
          '&:hover': {
            bgcolor: alpha('#fff', 0.15),
          },
        }),
});

const headerMenuPaperSx = (theme: any, isDarkMode: boolean) => ({
  elevation: 0,
  overflow: 'visible',
  filter: isDarkMode
    ? 'drop-shadow(0px 8px 24px rgba(0,0,0,0.55))'
    : 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
  mt: 1.5,
  ...(isDarkMode && {
    bgcolor: theme.palette.background.paper,
    border: `1px solid ${alpha('#3b82f6', 0.18)}`,
    backgroundImage: 'none',
  }),
});

// Estilos dos Menus (Profile e Notifications)
const menuStyles = {
  notifications: (isMobile: boolean, isDarkMode: boolean, theme: any) => ({
    ...headerMenuPaperSx(theme, isDarkMode),
    minWidth: isMobile ? 280 : 300,
    maxWidth: isMobile ? '90vw' : 400,
    '& .MuiMenuItem-root': {
      minHeight: isMobile ? 48 : 40,
      fontSize: isMobile ? '0.875rem' : '0.75rem',
      whiteSpace: 'normal' as const,
      wordWrap: 'break-word' as const,
    },
  }),
};

interface HeaderProps {
  onDrawerToggle: () => void;
  onProfileMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  sidebarOpen?: boolean;
  sidebarHovered?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  onDrawerToggle,
  onProfileMenuOpen,
  sidebarOpen = true,
  sidebarHovered = false,
}) => {
  const theme = useTheme();
  const sidebarExpanded = sidebarOpen || sidebarHovered;
  const navigate = useNavigate();
  const { isDarkMode, themeId } = useCustomTheme();
  const isCopa = isCopaTheme(themeId);

  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);

  // Estados para pesquisa
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchMenuAnchor, setSearchMenuAnchor] = useState<null | HTMLElement>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Estado para modal de visualização de fatura
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);

  // Breakpoints mais precisos para melhor responsividade
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Efeito para limpar o menu apenas quando o campo de busca é limpo completamente
  // Não remover o anchor se uma busca foi realizada (mesmo sem resultados)
  // O anchor será gerenciado pela função performSearch e clearSearch

  // Efeito para fechar menu de pesquisa ao clicar fora
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const searchContainer = document.querySelector('[data-search-container]');
      const searchMenu = document.querySelector('[role="menu"]');

      if (searchMenuAnchor &&
        searchContainer &&
        !searchContainer.contains(target) &&
        (!searchMenu || !searchMenu.contains(target))) {
        setSearchMenuAnchor(null);
      }
    };

    if (searchMenuAnchor) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [searchMenuAnchor]);

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchor(null);
  };

  // Funções para pesquisa
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);

    if (value.length === 0) {
      setSearchResults([]);
      setSearchMenuAnchor(null);
      setHasSearched(false);
    }
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && searchValue.trim().length > 0) {
      performSearch(searchValue.trim());
    }
  };

  const performSearch = async (query: string) => {
    setIsSearching(true);
    setHasSearched(true);
    let results: any[] = [];

    try {
      // Detectar prefixo e fazer busca específica
      const trimmedQuery = query.trim();
      
      if (trimmedQuery.length === 0) {
        setSearchResults([]);
        setHasSearched(false);
        setIsSearching(false);
        return;
      }

      // Verificar se começa com prefixo
      const firstChar = trimmedQuery.charAt(0).toLowerCase();
      const searchTerm = trimmedQuery.substring(1).trim();

      if (firstChar === 'c' && searchTerm.length > 0) {
        // Buscar cliente por nome
        const response = await clientService.getAll({ search: searchTerm, limit: 10 });
        if (response.success && response.data) {
          results = response.data.map((client: any) => ({
            type: 'cliente',
            id: client.id,
            name: client.nome,
            email: client.cpf_cnpj || '',
            client: client.nome,
          }));
        }
      } else if (firstChar === 'e' && searchTerm.length > 0) {
        // Buscar equipamento por número de série
        const response = await equipmentService.getEquipments({ search: searchTerm, limit: 10 });
        if (response.success && response.data) {
          results = response.data.map((equipment: any) => ({
            type: 'equipamento',
            id: equipment.id,
            name: equipment.modelo?.titulo || 'Equipamento',
            client: equipment.contrato?.cliente?.nome || 'Sem cliente',
            model: equipment.modelo?.titulo || '',
            status: equipment.status_uso || '',
            numero_serie: equipment.numero_serie || '',
          }));
        }
      } else if (firstChar === 'f' && searchTerm.length > 0) {
        // Buscar fatura por número (ID)
        // Se o termo de busca for um número, buscar diretamente pelo ID
        const invoiceId = parseInt(searchTerm, 10);
        if (!isNaN(invoiceId)) {
          try {
            const response = await invoiceService.getInvoiceById(invoiceId);
            if (response && response.success && response.data) {
              const invoice = response.data;
              // Buscar resumo de pagamentos se a fatura estiver paga
              let paymentInfo = null;
              if (invoice.status === 'pago' || invoice.status === 'parcialmente_pago') {
                try {
                  const summary = await paymentService.getPaymentSummary(
                    invoice.id,
                    Number(invoice.valor),
                    Number(invoice.acrescimo || 0),
                    Number(invoice.desconto || 0)
                  );
                  paymentInfo = {
                    totalPaid: summary.totalPaid,
                    lastPaymentDate: summary.payments.length > 0 
                      ? summary.payments[summary.payments.length - 1].data_pagamento 
                      : invoice.data_pagamento
                  };
                } catch (error) {
                  console.error('Erro ao buscar resumo de pagamentos:', error);
                }
              }
              
              results = [{
                type: 'fatura',
                id: invoice.id,
                client: invoice.cliente?.nome || 'Cliente não encontrado',
                contractTitle: invoice.contrato?.titulo || null,
                dataVencimento: invoice.data_vencimento,
                status: invoice.status || 'aberto',
                amount: invoice.valor || 0,
                paymentInfo: paymentInfo
              }];
            }
          } catch (error: any) {
            // Se for 404 (fatura não encontrada), tratar silenciosamente sem mostrar toast
            if (error.response?.status === 404) {
              // Fatura não encontrada - apenas retornar lista vazia sem erro
              results = [];
            } else {
              // Para outros erros, tentar busca geral
              try {
                const response = await invoiceService.getInvoices({ limit: 10 });
                if (response && response.success && response.data && Array.isArray(response.data)) {
                  // Filtrar faturas que contenham o termo no ID
                  const filtered = response.data.filter((invoice: any) => 
                    invoice && invoice.id && invoice.id.toString().includes(searchTerm)
                  );
                  // Processar cada fatura e buscar informações de pagamento se necessário
                  results = await Promise.all(filtered.map(async (invoice: any) => {
                    let paymentInfo = null;
                    if (invoice.status === 'pago' || invoice.status === 'parcialmente_pago') {
                      try {
                        const summary = await paymentService.getPaymentSummary(
                          invoice.id,
                          Number(invoice.valor || 0),
                          Number(invoice.acrescimo || 0),
                          Number(invoice.desconto || 0)
                        );
                        paymentInfo = {
                          totalPaid: summary.totalPaid,
                          lastPaymentDate: summary.payments.length > 0 
                            ? summary.payments[summary.payments.length - 1].data_pagamento 
                            : invoice.data_pagamento
                        };
                      } catch (error) {
                        console.error('Erro ao buscar resumo de pagamentos:', error);
                      }
                    }
                    
                    return {
                      type: 'fatura',
                      id: invoice.id,
                      client: invoice.cliente?.nome || 'Cliente não encontrado',
                      contractTitle: invoice.contrato?.titulo || null,
                      dataVencimento: invoice.data_vencimento,
                      status: invoice.status || 'aberto',
                      amount: invoice.valor || 0,
                      paymentInfo: paymentInfo
                    };
                  }));
                }
              } catch (fallbackError) {
                console.error('Erro na busca alternativa de faturas:', fallbackError);
                results = [];
              }
            }
          }
        } else {
          // Se não for número, fazer busca geral
          try {
            const response = await invoiceService.getInvoices({ limit: 10 });
            if (response && response.success && response.data && Array.isArray(response.data)) {
              // Filtrar faturas que contenham o termo no ID ou nome do cliente
              const filtered = response.data.filter((invoice: any) => 
                invoice && (
                  (invoice.id && invoice.id.toString().includes(searchTerm)) ||
                  (invoice.cliente?.nome && invoice.cliente.nome.toLowerCase().includes(searchTerm.toLowerCase()))
                )
              );
              // Processar cada fatura e buscar informações de pagamento se necessário
              results = await Promise.all(filtered.map(async (invoice: any) => {
                let paymentInfo = null;
                if (invoice.status === 'pago' || invoice.status === 'parcialmente_pago') {
                  try {
                    const summary = await paymentService.getPaymentSummary(
                      invoice.id,
                      Number(invoice.valor || 0),
                      Number(invoice.acrescimo || 0),
                      Number(invoice.desconto || 0)
                    );
                    paymentInfo = {
                      totalPaid: summary.totalPaid,
                      lastPaymentDate: summary.payments.length > 0 
                        ? summary.payments[summary.payments.length - 1].data_pagamento 
                        : invoice.data_pagamento
                    };
                  } catch (error) {
                    console.error('Erro ao buscar resumo de pagamentos:', error);
                  }
                }
                
                return {
                  type: 'fatura',
                  id: invoice.id,
                  client: invoice.cliente?.nome || 'Cliente não encontrado',
                  contractTitle: invoice.contrato?.titulo || null,
                  dataVencimento: invoice.data_vencimento,
                  status: invoice.status || 'aberto',
                  amount: invoice.valor || 0,
                  paymentInfo: paymentInfo
                };
              }));
            }
          } catch (error) {
            console.error('Erro ao buscar faturas:', error);
            results = [];
          }
        }
      } else {
        // Busca geral (sem prefixo) - buscar em todos os tipos
        try {
          const promises = [
            clientService.getAll({ search: trimmedQuery, limit: 5 }).catch(err => {
              console.error('Erro ao buscar clientes:', err);
              return { success: false, data: [] };
            }),
            equipmentService.getEquipments({ search: trimmedQuery, limit: 5 }).catch(err => {
              console.error('Erro ao buscar equipamentos:', err);
              return { success: false, data: [] };
            }),
            invoiceService.getInvoices({ search: trimmedQuery, limit: 5 }).catch(err => {
              console.error('Erro ao buscar faturas:', err);
              return { success: false, data: [] };
            }),
          ];

          const [clientsResponse, equipmentsResponse, invoicesResponse] = await Promise.all(promises);

          // Processar clientes
          if (clientsResponse && clientsResponse.success && clientsResponse.data && Array.isArray(clientsResponse.data)) {
            results.push(...clientsResponse.data.map((client: any) => ({
              type: 'cliente',
              id: client.id,
              name: client.nome,
              email: client.cpf_cnpj || '',
              client: client.nome,
            })));
          }

          // Processar equipamentos
          if (equipmentsResponse && equipmentsResponse.success && equipmentsResponse.data && Array.isArray(equipmentsResponse.data)) {
            results.push(...equipmentsResponse.data.map((equipment: any) => ({
              type: 'equipamento',
              id: equipment.id,
              name: equipment.modelo?.titulo || 'Equipamento',
              client: equipment.contrato?.cliente?.nome || 'Sem cliente',
              model: equipment.modelo?.titulo || '',
              status: equipment.status_uso || '',
              numero_serie: equipment.numero_serie || '',
            })));
          }

          // Processar faturas
          if (invoicesResponse && invoicesResponse.success && invoicesResponse.data && Array.isArray(invoicesResponse.data)) {
            const invoiceResults = await Promise.all(invoicesResponse.data.map(async (invoice: any) => {
              let paymentInfo = null;
              if (invoice.status === 'pago' || invoice.status === 'parcialmente_pago') {
                try {
                  const summary = await paymentService.getPaymentSummary(
                    invoice.id,
                    Number(invoice.valor || 0),
                    Number(invoice.acrescimo || 0),
                    Number(invoice.desconto || 0)
                  );
                  paymentInfo = {
                    totalPaid: summary.totalPaid,
                    lastPaymentDate: summary.payments.length > 0 
                      ? summary.payments[summary.payments.length - 1].data_pagamento 
                      : invoice.data_pagamento
                  };
                } catch (error) {
                  console.error('Erro ao buscar resumo de pagamentos:', error);
                }
              }
              
              return {
                type: 'fatura',
                id: invoice.id,
                client: invoice.cliente?.nome || 'Cliente não encontrado',
                contractTitle: invoice.contrato?.titulo || null,
                dataVencimento: invoice.data_vencimento,
                status: invoice.status || 'aberto',
                amount: invoice.valor || 0,
                paymentInfo: paymentInfo
              };
            }));
            results.push(...invoiceResults);
          }
        } catch (error) {
          console.error('Erro na busca geral:', error);
          results = [];
        }
      }

      setSearchResults(results);
    } catch (error) {
      console.error('Erro na pesquisa:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);

      // Mostrar menu mesmo se não houver resultados (para exibir mensagem)
      // Sempre definir o anchor após a busca, independente de ter resultados
      const searchField = document.querySelector('input[placeholder*="Pesquisar"]') as HTMLElement;
      if (searchField) {
        // Usar setTimeout para garantir que o DOM esteja atualizado
        setTimeout(() => {
          setSearchMenuAnchor(searchField);
        }, 0);
      }
    }
  };

  const handleSearchResultClick = (result: any) => {
    setSearchMenuAnchor(null);
    setSearchValue('');
    setSearchResults([]);

    switch (result.type) {
      case 'cliente':
        navigate(`/clients/${result.id}`);
        break;
      case 'fatura':
        // Abrir modal de visualização da fatura
        setSelectedInvoiceId(result.id);
        setInvoiceModalOpen(true);
        break;
      case 'chamado':
        navigate(`/tickets/${result.id}`);
        break;
      case 'equipamento':
        navigate(`/equipment/${result.id}`);
        break;
    }
  };

  const clearSearch = () => {
    setSearchValue('');
    setSearchResults([]);
    setSearchMenuAnchor(null);
    setHasSearched(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          ...getAppBarLayoutSx(theme, isMobile, sidebarExpanded),
          background: getHeaderAppBarBackground(theme, themeId),
          backgroundColor: isDarkMode || isCopa ? undefined : theme.palette.primary.main,
          color: isCopa ? '#1a3328' : isDarkMode ? 'inherit' : '#ffffff',
          borderBottom: isDarkMode
            ? `1px solid ${alpha('#3b82f6', 0.22)}`
            : isCopa
              ? `1px solid ${alpha('#013f1e', 0.12)}`
              : 'none',
        }}
      >
        <Toolbar sx={toolbarStyles.main}>
          {/* botão de colapsar menu lateral */}
          <IconButton
            aria-label="toggle drawer"
            edge="start"
            onClick={onDrawerToggle}
            sx={menuButtonStyles(theme, isDarkMode, isCopa)}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo — mobile ou tema Copa no desktop (escuro usa sidebar) */}
          {(isMobile || isCopa) && (
            <Box
              component="img"
              src={isCopa ? logo : logoWhite}
              alt="SysUp Logo"
              sx={{
                ...logoStyles,                
                ml: isCopa && isMobile ? 2 : 2,
                cursor: 'pointer',
              }}
              onClick={() => navigate('/dashboard')}
            />
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Ações do header: tema, alertas, perfil */}
          <Box sx={headerActionsGroupSx}>
            <ThemeMenuButton
              isDarkMode={isDarkMode}
              isCopa={isCopa}
              size={isMobile ? 'large' : 'medium'}
            />

            <Tooltip title="Notificações">
              <IconButton
                color="inherit"
                onClick={handleNotificationMenuOpen}
                size={isMobile ? 'large' : 'medium'}
                sx={headerActionButtonSx(isDarkMode, isCopa)}
              >
                <Badge
                  badgeContent={3}
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': isDarkMode
                      ? { boxShadow: `0 0 0 2px ${alpha('#0a101f', 0.9)}` }
                      : {},
                  }}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Perfil">
              <IconButton
                size="medium"
                edge="end"
                aria-label="account of current user"
                aria-haspopup="true"
                onClick={onProfileMenuOpen}
                color="inherit"
                sx={{
                  ...headerActionButtonSx(isDarkMode, isCopa),
                  ml: 0.25,
                  p: 0.5,
                }}
              >
                <UserAvatar
                  size={isMobile ? 36 : 30}
                  showUploadButton={false}
                  editable={false}
                  sx={{
                    bgcolor: isDarkMode ? alpha('#818cf8', 0.85) : 'secondary.main',
                    border: isDarkMode
                      ? `2px solid ${alpha('#60a5fa', 0.65)}`
                      : '2px solid white',
                    boxShadow: isDarkMode
                      ? `0 0 14px ${alpha('#3b82f6', 0.35)}`
                      : '0 4px 12px rgba(0,0,0,0.15)',
                    borderRadius: '50%',
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationMenuClose}
        PaperProps={{
          elevation: 0,
          sx: menuStyles.notifications(isMobile, isDarkMode, theme),
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem>
          <Typography variant="subtitle2">Novo chamado #1234</Typography>
        </MenuItem>
        <MenuItem>
          <Typography variant="subtitle2">Pagamento confirmado</Typography>
        </MenuItem>
        <MenuItem>
          <Typography variant="subtitle2">Cliente cadastrado</Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleNotificationMenuClose}>
          <Typography variant="body2" color="primary">
            Ver todas as notificações
          </Typography>
        </MenuItem>
      </Menu>

      {/* Modal de Visualização de Fatura */}
      {selectedInvoiceId && (
        <InvoiceViewModal
          open={invoiceModalOpen}
          onClose={() => {
            setInvoiceModalOpen(false);
            setSelectedInvoiceId(null);
          }}
          invoice_id={selectedInvoiceId}
        />
      )}
    </>
  );
};

export default Header; 